import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

// Components
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import LoadingScreen from './components/LoadingScreen'
import Login from './components/Login'
import Register from './components/Register'
import LoginDebug from './components/LoginDebug'
import QuickSignup from './components/QuickSignup'
import ConnectionTestPage from './components/ConnectionTestPage'

// Pages
import Home from './pages/Home'
import Assessment from './pages/Assessment'
import Dashboard from './pages/Dashboard'
import AgencyDashboard from './pages/AgencyDashboard'
import TaskReminders from './pages/TaskReminders'
import PolicyLibrary from './pages/PolicyLibrary'
import LearningCenter from './pages/LearningCenter'
import IncidentReporter from './pages/IncidentReporter'
import IncidentDashboard from './pages/IncidentDashboard'
import Profile from './pages/Profile'
import About from './pages/About'
import AdminDashboard from './components/AdminDashboard'
import TestApprovalWorkflow from './pages/TestApprovalWorkflow'
import SimpleApprovalTest from './pages/SimpleApprovalTest'

// Context
import { AuthProvider, useAuth } from './context/AuthContext'
import { UserProvider } from './context/UserContext'
import { AssessmentProvider } from './context/AssessmentContext'

function App() {
  const [isLoading, setIsLoading] = useState(true)
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    // Simulate app initialization
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 2000)

    // Handle online/offline status
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <AuthProvider>
      <UserProvider>
        <AssessmentProvider>
          <Router>
            <AppContent isOnline={isOnline} />
          </Router>
        </AssessmentProvider>
      </UserProvider>
    </AuthProvider>
  )
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth()
  
  if (loading) {
    return <LoadingScreen />
  }
  
  return user ? children : <Navigate to="/login" replace />
}

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth()
  
  if (loading) {
    return <LoadingScreen />
  }
  
  return user ? <Navigate to="/dashboard" replace /> : children
}

// App Content Component
const AppContent = ({ isOnline }) => {
  const { user } = useAuth()
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Offline indicator */}
      <AnimatePresence>
        {!isOnline && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0 }}
            className="bg-warning-500 text-white text-center py-2 px-4 text-sm font-medium"
          >
            📡 You're offline. Some features may be limited.
          </motion.div>
        )}
      </AnimatePresence>

      {/* Only show Navbar if user is authenticated */}
      {user && <Navbar />}
      
      <main className="flex-1">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          <Route path="/" element={
            <PublicRoute>
              <Home />
            </PublicRoute>
          } />
          <Route path="/about" element={<About />} />
          <Route path="/debug" element={<LoginDebug />} />
          <Route path="/signup" element={<QuickSignup />} />
          <Route path="/connection-test" element={<ConnectionTestPage />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/agency-dashboard" element={
            <ProtectedRoute>
              <AgencyDashboard />
            </ProtectedRoute>
          } />
          <Route path="/assessment" element={
            <ProtectedRoute>
              <Assessment />
            </ProtectedRoute>
          } />
          <Route path="/task-reminders" element={
            <ProtectedRoute>
              <TaskReminders />
            </ProtectedRoute>
          } />
          <Route path="/policy-library" element={
            <ProtectedRoute>
              <PolicyLibrary />
            </ProtectedRoute>
          } />
          <Route path="/learning-center" element={
            <ProtectedRoute>
              <LearningCenter />
            </ProtectedRoute>
          } />
          <Route path="/incident-reporter" element={
            <ProtectedRoute>
              <IncidentReporter />
            </ProtectedRoute>
          } />
          <Route path="/incident-dashboard" element={
            <ProtectedRoute>
              <IncidentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard currentUser={user} />
            </ProtectedRoute>
          } />
          <Route path="/test-approval-workflow" element={
            <ProtectedRoute>
              <TestApprovalWorkflow />
            </ProtectedRoute>
          } />
          <Route path="/simple-approval-test" element={
            <ProtectedRoute>
              <SimpleApprovalTest />
            </ProtectedRoute>
          } />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to={user ? "/dashboard" : "/"} replace />} />
        </Routes>
      </main>
      
      {/* Only show Footer if user is authenticated */}
      {user && <Footer />}
    </div>
  )
}

export default App